import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatTL, formatDateTime, formatDuration } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plate } from "@/components/Plate";
import { BusinessBadge } from "@/components/BusinessTag";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Money, CreditCard, Bank, Wallet, Percent, TicketIcon, Receipt,
  FunnelSimple, DownloadSimple, X, CaretDown, CaretUp, PencilSimple, Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const METHOD_META = {
  cash:          { label: "Nakit",       icon: Money,      accent: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
  card:          { label: "Kredi Kartı", icon: CreditCard, accent: "text-blue-600",    bg: "bg-blue-50 border-blue-200" },
  bank_transfer: { label: "EFT/Havale",  icon: Bank,       accent: "text-purple-600",  bg: "bg-purple-50 border-purple-200" },
  wallet:        { label: "Cüzdan",      icon: Wallet,     accent: "text-amber-600",   bg: "bg-amber-50 border-amber-200" },
  other:         { label: "Diğer",       icon: Receipt,    accent: "text-slate-600",   bg: "bg-slate-50 border-slate-200" },
};

const PERIOD_OPTS = [
  { key: "day", label: "Bugün" },
  { key: "week", label: "Bu Hafta" },
  { key: "month", label: "Bu Ay" },
  { key: "year", label: "Bu Yıl" },
  { key: "custom", label: "Özel Tarih" },
];

function todayLocal() {
  return new Date().toISOString().slice(0, 10);
}

export default function PaymentsLedger() {
  const { user: authUser } = useAuth();
  const isSuperAdmin = authUser?.role === "super_admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState(searchParams.get("period") || "day");
  const [start, setStart] = useState(todayLocal());
  const [end, setEnd] = useState(todayLocal());
  const [method, setMethod] = useState("all");   // 'all' or one of METHOD keys
  const [plate, setPlate] = useState("");
  const [operator, setOperator] = useState("all");
  const [lot, setLot] = useState("all");
  const [onlyDiscount, setOnlyDiscount] = useState(
    searchParams.get("only_discount") === "1"
  );
  const [showFilters, setShowFilters] = useState(false);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);   // { id, plate, fee, paid_via }
  const [saving, setSaving] = useState(false);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (period === "custom") {
      const sIso = new Date(start + "T00:00:00").toISOString();
      const eIso = new Date(end + "T23:59:59.999").toISOString();
      p.set("start", sIso); p.set("end", eIso);
    } else {
      p.set("period", period);
    }
    if (method !== "all") p.set("method", method);
    if (plate.trim()) p.set("plate", plate.trim().toUpperCase());
    if (operator !== "all") p.set("operator_email", operator);
    if (lot !== "all") p.set("lot", lot);
    if (onlyDiscount) p.set("only_discount", "true");
    return p.toString();
    // eslint-disable-next-line
  }, [period, start, end, method, plate, operator, lot, onlyDiscount]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/payments/ledger?${params}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yüklenemedi");
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [params]);

  const clearFilters = () => {
    setPeriod("day"); setStart(todayLocal()); setEnd(todayLocal());
    setMethod("all"); setPlate(""); setOperator("all"); setLot("all");
    setOnlyDiscount(false);
    setSearchParams({}, { replace: true });
  };

  // Effective items — backend already filters when `onlyDiscount=true`, so
  // shownItems is just data.items. Kept as a variable for consistent naming.
  const shownItems = data?.items || [];
  const shownDiscountSum = data?.discount_sum || 0;

  const rangeLabel = () => {
    if (!data?.range) return "";
    const s = new Date(data.range.start);
    const e = new Date(data.range.end);
    return `${s.toLocaleDateString("tr-TR")} → ${e.toLocaleDateString("tr-TR")}`;
  };

  const openReceipt = async (id) => {
    try {
      const token = localStorage.getItem("otopark_token");
      const r = await fetch(`${BACKEND_URL}/api/sessions/${id}/receipt`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const b = await r.blob();
      const url = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = url; a.target = "_blank"; a.rel = "noopener";
      a.download = `makbuz_${id.slice(-8)}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (e) { toast.error("Makbuz açılamadı"); }
  };

  const openEdit = (r) => {
    setEditing({
      id: r.id, plate: r.plate,
      fee: r.fee, paid_via: r.paid_via || "cash",
      original_fee: r.original_fee, discount_amount: r.discount_amount || 0,
    });
  };
  const saveEdit = async () => {
    if (!editing) return;
    const feeNum = parseFloat(editing.fee);
    if (isNaN(feeNum) || feeNum < 0) {
      return toast.error("Geçerli tutar girin");
    }
    setSaving(true);
    try {
      await api.patch(`/sessions/${editing.id}`, {
        fee: feeNum,
        paid_via: editing.paid_via,
        payment_status: "odendi",
      });
      toast.success("Tahsilat güncellendi — Kasa & raporlar otomatik yenilendi");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    } finally { setSaving(false); }
  };
  const deleteRow = async (r) => {
    if (!window.confirm(
      `Bu tahsilat kaydını (oturum) silmek istediğinize emin misiniz?\n\n` +
      `Plaka: ${r.plate}\nTutar: ${formatTL(r.fee)}\n` +
      `Yöntem: ${METHOD_META[r.paid_via]?.label || r.paid_via}\n\n` +
      `⚠️ Bu ödeme kaydı ve ilişkili oturum tamamen silinecek.\n` +
      `Kasa/Banka toplamları etkilenir. Bu işlem geri alınamaz.`
    )) return;
    try {
      await api.delete(`/sessions/${r.id}`);
      toast.success("Tahsilat silindi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const exportCsv = () => {
    if (!data?.items?.length) return toast.error("İhraç için veri yok");
    const headers = [
      "Plaka", "Otopark", "Giriş", "Çıkış", "Ödeme Zamanı", "Süre (dk)",
      "Orij. Ücret", "İndirim", "İndirim Sebebi", "İndirim Uygulayan",
      "Kupon", "Tahsil Edilen", "Yöntem", "Tahsil Eden", "Not",
    ];
    const rows = data.items.map((r) => [
      r.plate, r.parking_lot_key === "ust_kat" ? "Üst Kat" : "Alt Kat",
      formatDateTime(r.entry_time), formatDateTime(r.exit_time), formatDateTime(r.paid_at),
      r.duration_minutes || 0,
      Number(r.original_fee || 0).toFixed(2),
      Number(r.discount_amount || 0).toFixed(2),
      r.discount_reason || "",
      r.discount_by_email || "",
      r.coupon_code || "",
      Number(r.fee || 0).toFixed(2),
      METHOD_META[r.paid_via]?.label || r.paid_via || "",
      r.paid_by_operator_email || "",
      r.payment_note || "",
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
    const csv = "\uFEFF" + headers.join(",") + "\n" + rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `odemeler_${(data.range.start || "").slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV indirildi");
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Muhasebe</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Tahsilatlar</h1>
          <div className="text-sm text-muted-foreground mt-1" data-testid="ledger-range-label">
            {rangeLabel()} · {data?.total_count ?? 0} tahsilat · <b className="text-primary font-mono">{formatTL(data?.grand_total || 0)}</b>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFilters((v) => !v)}
            data-testid="ledger-toggle-filters"
            className="rounded-sm uppercase tracking-widest text-xs"
          >
            <FunnelSimple size={14} /> Filtreler {showFilters ? <CaretUp size={12} /> : <CaretDown size={12} />}
          </Button>
          <Button
            onClick={exportCsv}
            data-testid="ledger-export-csv"
            className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-semibold"
          >
            <DownloadSimple size={14} /> CSV İndir
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card className="rounded-sm border-border">
          <CardContent className="p-5 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div>
                <Label className="text-xs">Dönem</Label>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger className="rounded-sm h-9" data-testid="ledger-period"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PERIOD_OPTS.map((p) => (
                      <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {period === "custom" && (
                <>
                  <div>
                    <Label className="text-xs">Başlangıç</Label>
                    <Input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                           className="rounded-sm h-9" data-testid="ledger-start-date" />
                  </div>
                  <div>
                    <Label className="text-xs">Bitiş</Label>
                    <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                           className="rounded-sm h-9" data-testid="ledger-end-date" />
                  </div>
                </>
              )}
              <div>
                <Label className="text-xs">Plaka Ara</Label>
                <Input value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())}
                       className="rounded-sm h-9 font-mono uppercase"
                       placeholder="34ABC..." data-testid="ledger-plate-filter" />
              </div>
              <div>
                <Label className="text-xs">Otopark</Label>
                <Select value={lot} onValueChange={setLot}>
                  <SelectTrigger className="rounded-sm h-9" data-testid="ledger-lot-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    <SelectItem value="ust_kat">Üst Kat</SelectItem>
                    <SelectItem value="alt_kat">Alt Kat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Tahsil Eden</Label>
                <Select value={operator} onValueChange={setOperator}>
                  <SelectTrigger className="rounded-sm h-9" data-testid="ledger-operator-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    {(data?.operators || []).map((op) => (
                      <SelectItem key={op} value={op}>{op}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end">
              <Button variant="ghost" size="sm" onClick={clearFilters}
                      data-testid="ledger-clear-filters"
                      className="uppercase tracking-widest text-[10px]">
                <X size={12} /> Filtreleri Temizle
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment method totals — click to filter */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {/* All-methods card */}
        <MethodCard
          activeKey={method}
          methodKey="all"
          label="Tüm Yöntemler"
          icon={Receipt}
          accent="text-primary"
          bg="bg-primary/5 border-primary/30"
          amount={data?.grand_total || 0}
          count={data?.total_count || 0}
          onClick={() => setMethod("all")}
          testid="ledger-total-all"
        />
        {["cash", "card", "bank_transfer", "wallet"].map((k) => {
          const M = METHOD_META[k];
          return (
            <MethodCard
              key={k}
              activeKey={method}
              methodKey={k}
              label={M.label}
              icon={M.icon}
              accent={M.accent}
              bg={M.bg}
              amount={data?.totals?.[k]?.amount || 0}
              count={data?.totals?.[k]?.count || 0}
              onClick={() => setMethod(method === k ? "all" : k)}
              testid={`ledger-total-${k}`}
            />
          );
        })}
      </div>

      {/* Discount summary */}
      {(data?.discount_sum || 0) > 0 && (
        <Card
          className={`rounded-sm ${onlyDiscount ? "border-amber-400 bg-amber-100" : "border-amber-200 bg-amber-50/50 cursor-pointer hover:bg-amber-100"}`}
          onClick={() => !onlyDiscount && setOnlyDiscount(true)}
          data-testid="ledger-discount-card"
        >
          <CardContent className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <Percent size={16} className="text-amber-600" />
              <span className="uppercase tracking-widest text-xs font-semibold">
                {onlyDiscount ? "Sadece İndirimli Oturumlar" : "Toplam Uygulanan İndirim"}
              </span>
              {onlyDiscount && (
                <Button
                  variant="ghost" size="sm"
                  className="h-6 px-2 text-[10px]"
                  onClick={(e) => { e.stopPropagation(); setOnlyDiscount(false); }}
                  data-testid="ledger-clear-discount-filter"
                >
                  <X size={10} /> Kaldır
                </Button>
              )}
            </div>
            <div className="font-mono font-bold text-amber-700" data-testid="ledger-discount-sum">
              -{formatTL(shownDiscountSum)} <span className="text-xs text-muted-foreground">({data.discount_count || shownItems.length} işlem)</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card className="rounded-sm border-border">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plaka</TableHead>
                <TableHead>Otopark</TableHead>
                <TableHead>Giriş → Çıkış</TableHead>
                <TableHead>Süre</TableHead>
                <TableHead className="text-right">Orij. Ücret</TableHead>
                <TableHead className="text-right">İndirim</TableHead>
                <TableHead>İnd. Uygulayan</TableHead>
                <TableHead className="text-right">Tahsil</TableHead>
                <TableHead>Yöntem</TableHead>
                <TableHead>Tahsil Eden</TableHead>
                <TableHead>Zaman</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={12} className="text-center py-8 text-muted-foreground">Yükleniyor...</TableCell></TableRow>
              )}
              {!loading && (shownItems.length === 0) && (
                <TableRow><TableCell colSpan={12} className="text-center py-8 text-muted-foreground">{onlyDiscount ? "Bu dönemde indirim uygulanmış oturum yok" : "Bu filtrelerle kayıt yok"}</TableCell></TableRow>
              )}
              {shownItems.map((r) => {
                const M = METHOD_META[r.paid_via] || METHOD_META.other;
                const Icon = M.icon;
                return (
                  <TableRow key={r.id} data-testid={`ledger-row-${r.plate}`}>
                    <TableCell>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Plate country={r.country_code}>{r.plate}</Plate>
                        <BusinessBadge session={r} />
                      </div>
                    </TableCell>
                    <TableCell className="text-xs uppercase tracking-widest">
                      {r.parking_lot_key === "ust_kat" ? "Üst" : "Alt"}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      <div>{formatDateTime(r.entry_time)}</div>
                      <div className="text-muted-foreground">↓ {formatDateTime(r.exit_time)}</div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{formatDuration(r.duration_minutes)}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-muted-foreground">
                      {r.discount_amount > 0 ? formatTL(r.original_fee)
                        : (r.business_account_id && r.pre_business_fee != null && r.pre_business_fee !== r.fee ? formatTL(r.pre_business_fee) : "—")}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {r.discount_amount > 0 ? (
                        <span className="text-amber-600 font-semibold">-{formatTL(r.discount_amount)}</span>
                      ) : (r.business_account_id && r.business_agreement_type === "first_hours" && r.business_fee > 0 ? (
                        <span className="text-violet-700 font-semibold" data-testid={`ledger-business-fee-${r.plate}`}>
                          İşletme ₺{Number(r.business_fee).toFixed(0)}
                          <div className="text-[9px] uppercase tracking-wider">{r.business_payment_status === "tahsil_edildi" ? "tahsil edildi" : "işletmeden bekliyor"}</div>
                        </span>
                      ) : (r.business_account_id && r.business_agreement_type === "quota" ? (
                        <span className="text-violet-700 font-semibold text-[10px] uppercase">{r.business_over_quota ? "Kontenjan dışı" : "Kontenjan · muaf"}</span>
                      ) : "—"))}
                      {r.coupon_code && (
                        <div className="text-[10px] text-primary flex items-center justify-end gap-1">
                          <TicketIcon size={10} />{r.coupon_code}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {r.discount_by_email ? (
                        <div>
                          <div className="font-semibold">{r.discount_by_email}</div>
                          {r.discount_reason && <div className="text-[10px] text-muted-foreground">{r.discount_reason}</div>}
                        </div>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold text-primary">
                      {formatTL(r.fee)}
                    </TableCell>
                    <TableCell>
                      <Badge className={`rounded-sm ${M.bg} ${M.accent} border-transparent gap-1 text-[10px] uppercase tracking-widest font-semibold`}>
                        <Icon size={10} /> {M.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">{r.paid_by_operator_email || "—"}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{formatDateTime(r.paid_at)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => openReceipt(r.id)}
                                data-testid={`ledger-receipt-${r.plate}`} title="Makbuz"
                                className="h-8 w-8 p-0">
                          <Receipt size={14} />
                        </Button>
                        {isSuperAdmin && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => openEdit(r)}
                                    data-testid={`ledger-edit-${r.id}`}
                                    title="Süperadmin: Tutar/yöntem düzenle"
                                    className="h-8 w-8 p-0 text-primary">
                              <PencilSimple size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => deleteRow(r)}
                                    data-testid={`ledger-delete-${r.id}`}
                                    title="Süperadmin: Tahsilatı sil"
                                    className="h-8 w-8 p-0 text-destructive">
                              <Trash size={14} />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* SÜPERADMİN — Tahsilat Düzenle */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tahsilat Düzenle · {editing?.plate}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900">
                ⚠️ Süperadmine özel: Hatalı girilen tutar veya ödeme yöntemini
                düzeltmek için kullanın. Değişiklik audit log&apos;a yazılır ve
                Kasa/raporlar otomatik yenilenir.
              </div>
              <div>
                <Label>Tahsilat Tutarı (₺)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={editing.fee ?? ""}
                  onChange={(e) => setEditing({ ...editing, fee: e.target.value })}
                  className="rounded-sm font-mono"
                  data-testid="ledger-edit-fee-input" />
                {editing.original_fee && editing.original_fee !== editing.fee && (
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Orijinal ücret: {formatTL(editing.original_fee)}
                    {editing.discount_amount > 0 && ` · İndirim: -${formatTL(editing.discount_amount)}`}
                  </div>
                )}
              </div>
              <div>
                <Label>Ödeme Yöntemi</Label>
                <Select
                  value={editing.paid_via}
                  onValueChange={(v) => setEditing({ ...editing, paid_via: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="ledger-edit-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Nakit</SelectItem>
                    <SelectItem value="card">Kredi Kartı</SelectItem>
                    <SelectItem value="bank_transfer">EFT/Havale</SelectItem>
                    <SelectItem value="wallet">Cüzdan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>İptal</Button>
            <Button onClick={saveEdit} disabled={saving}
                    data-testid="ledger-edit-save"
                    className="bg-primary text-primary-foreground hover:bg-primary/90">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MethodCard({ activeKey, methodKey, label, icon: Icon, accent, bg, amount, count, onClick, testid }) {
  const active = activeKey === methodKey || (methodKey === "all" && activeKey === "all");
  const isFiltered = activeKey !== "all";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`p-4 rounded-sm border-2 text-left transition ${
        active && methodKey !== "all" ? `${bg} border-current ${accent} shadow-md` :
        active && methodKey === "all" && !isFiltered ? `${bg} border-current ${accent} shadow-md` :
        "bg-card border-border hover:border-muted-foreground"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <Icon size={18} weight={active ? "fill" : "regular"} className={active ? accent : "text-muted-foreground"} />
        {active && methodKey !== "all" && (
          <span className="text-[9px] uppercase tracking-widest bg-current text-white px-1.5 py-0.5 rounded-sm">Filtre</span>
        )}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-xl font-bold font-mono mt-1 ${active ? accent : ""}`}>
        {formatTL(amount)}
      </div>
      <div className="text-[10px] text-muted-foreground mt-0.5">{count} işlem</div>
    </button>
  );
}
